import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { Zap, Clock, Edit2, Check, X, Info, ToggleLeft, ToggleRight, Users, QrCode } from 'lucide-react';

// ── GraphQL ────────────────────────────────────────────────────────────────────

const GET_CONFIG = gql`
  query GetReferralConfig {
    referralConfig {
      id isEnabled
      firstScanBonusRecruiter firstScanBonusRecruited
      maxRecruits minScansBeforeCommission referralCodeExpireDays
    }
  }
`;

const UPDATE_CONFIG = gql`
  mutation UpdateReferralConfig($input: UpdateReferralConfigInput!) {
    updateReferralConfig(input: $input) {
      id isEnabled
      firstScanBonusRecruiter firstScanBonusRecruited
      maxRecruits minScansBeforeCommission referralCodeExpireDays
    }
  }
`;

const GET_ALL_REFERRALS = gql`
  query GetAllReferrals {
    allReferrals {
      id
      referralDate
      firstScanBonusPaid
      scanCount
      referrer { id firstName lastName email }
      referred { id firstName lastName }
    }
  }
`;

// ── Field component ────────────────────────────────────────────────────────────

const ConfigField = ({ label, hint, value, onChange, type = 'number', suffix = '', disabled = false }) => (
  <div className="flex items-start gap-3">
    <div className="flex-1">
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-2">{hint}</p>}
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <input
        type={type === 'number' ? 'number' : type}
        min={0}
        step="any"
        value={value}
        disabled={disabled}
        onChange={e => {
          if (type === 'number') {
            const n = parseFloat(e.target.value);
            onChange(isNaN(n) ? 0 : n);
          } else {
            onChange(e.target.value);
          }
        }}
        className={`w-28 border rounded-xl px-3 py-2 text-sm text-right font-semibold focus:outline-none focus:border-green-400 transition-colors ${
          disabled
            ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-default'
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      />
      {suffix && <span className="text-xs text-gray-500 font-medium w-10">{suffix}</span>}
    </div>
  </div>
);

// ── Section card ───────────────────────────────────────────────────────────────

const Section = ({ icon: Icon, title, color, children }) => {
  const colors = {
    green:  { bg: 'bg-green-50',  border: 'border-green-200', icon: 'text-green-600',  title: 'text-green-800' },
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  icon: 'text-blue-600',   title: 'text-blue-800' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200',icon: 'text-purple-600', title: 'text-purple-800' },
    gray:   { bg: 'bg-gray-50',   border: 'border-gray-200',  icon: 'text-gray-500',   title: 'text-gray-700' },
  };
  const c = colors[color] || colors.gray;
  return (
    <div className={`rounded-2xl border ${c.border} ${c.bg} p-5`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className={c.icon} />
        <h3 className={`text-sm font-bold ${c.title}`}>{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────

const fullName = (u) =>
  u ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || '—' : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ReferralPage = () => {
  const { data, loading, refetch } = useQuery(GET_CONFIG, { fetchPolicy: 'cache-and-network' });
  const { data: referralsData, loading: referralsLoading } = useQuery(GET_ALL_REFERRALS, { fetchPolicy: 'cache-and-network' });
  const [updateConfig, { loading: saving }] = useMutation(UPDATE_CONFIG);
  const [referralSearch, setReferralSearch] = useState('');

  const allReferrals = referralsData?.allReferrals ?? [];
  const filteredReferrals = referralSearch.trim()
    ? allReferrals.filter(r => {
        const q = referralSearch.toLowerCase();
        return (
          fullName(r.referrer).toLowerCase().includes(q) ||
          (r.referrer?.email ?? '').toLowerCase().includes(q) ||
          fullName(r.referred).toLowerCase().includes(q)
        );
      })
    : allReferrals;

  const original = data?.referralConfig;
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);

  const cfg = editing ? form : original;

  const startEdit = () => {
    setForm({ ...original });
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm(null);
    setEditing(false);
  };

  const handleSave = async () => {
    try {
      const { id, __typename, createdAt, updatedAt, ...input } = form;
      await updateConfig({ variables: { input } });
      await refetch();
      toast.success('Configuration sauvegardée');
      setEditing(false);
      setForm(null);
    } catch (e) {
      console.error('Save referral config error:', e);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const handleToggleEnabled = async () => {
    if (!original) return;
    try {
      await updateConfig({ variables: { input: { isEnabled: !original.isEnabled } } });
      toast.success(original.isEnabled ? 'Parrainage désactivé' : 'Parrainage activé');
    } catch {
      toast.error('Erreur');
    }
  };

  if (loading && !original) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="h-8 w-48 bg-gray-100 rounded-xl animate-pulse mb-2" />
        <div className="h-4 w-72 bg-gray-100 rounded-xl animate-pulse mb-8" />
        <div className="space-y-4">
          {[1,2,3,4].map(i => <div key={i} className="h-40 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parrainage</h1>
          <p className="text-sm text-gray-500 mt-1">Configurez les règles et récompenses du système de parrainage.</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Toggle enabled */}
          {original && (
            <button onClick={handleToggleEnabled} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50">
              {original.isEnabled
                ? <><ToggleRight size={18} className="text-green-600" /> Actif</>
                : <><ToggleLeft size={18} className="text-gray-400" /> Inactif</>}
            </button>
          )}
          {!editing
            ? <button onClick={startEdit} className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded-xl hover:bg-green-800"><Edit2 size={14} /> Modifier</button>
            : (
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-sm font-semibold rounded-xl hover:bg-green-800 disabled:opacity-50"><Check size={14} /> Sauvegarder</button>
                <button onClick={cancelEdit} className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-300"><X size={14} /> Annuler</button>
              </div>
            )
          }
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 text-sm text-blue-700">
        <Info size={16} className="shrink-0 mt-0.5" />
        <span>
          Les bonus et taux sont <strong>figés au moment du parrainage</strong>. Modifier cette config n'affecte que les <em>nouveaux</em> parrainages — les filleuls existants conservent leurs conditions d'origine.
        </span>
      </div>

      {cfg && (
        <div className="space-y-5">

          {/* Bonus 1er scan */}
          <Section icon={Zap} title="Bonus au 1er scan du filleul" color="green">
            <ConfigField
              label="Bonus recruteur"
              hint="DH crédités sur la cagnotte du parrain dès que son filleul effectue son 1er scan."
              value={editing ? form.firstScanBonusRecruiter : cfg.firstScanBonusRecruiter}
              onChange={set('firstScanBonusRecruiter')}
              suffix="DH"
              disabled={!editing}
            />
            <ConfigField
              label="Bonus recruté"
              hint="DH crédités sur la cagnotte du filleul lui-même à son 1er scan. Mettre à 0 pour désactiver."
              value={editing ? form.firstScanBonusRecruited : cfg.firstScanBonusRecruited}
              onChange={set('firstScanBonusRecruited')}
              suffix="DH"
              disabled={!editing}
            />
          </Section>

          {/* Règles générales */}
          <Section icon={Clock} title="Règles générales" color="gray">
            <ConfigField
              label="Scans avant bonus (Nth scan)"
              hint="Nombre de scans que le filleul doit atteindre pour déclencher le bonus parrain. 0 = dès le 1er scan."
              value={editing ? form.minScansBeforeCommission : cfg.minScansBeforeCommission}
              onChange={set('minScansBeforeCommission')}
              suffix="scans"
              disabled={!editing}
            />
            <ConfigField
              label="Max filleuls par parrain"
              hint="Nombre maximum de personnes qu'un utilisateur peut parrainer. 0 = illimité."
              value={editing ? form.maxRecruits : cfg.maxRecruits}
              onChange={set('maxRecruits')}
              suffix="max"
              disabled={!editing}
            />
            <ConfigField
              label="Validité du code (jours)"
              hint="Durée de validité du code de parrainage après création du compte. 0 = permanent."
              value={editing ? form.referralCodeExpireDays : cfg.referralCodeExpireDays}
              onChange={set('referralCodeExpireDays')}
              suffix="j"
              disabled={!editing}
            />
          </Section>

          {/* Summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-4">Résumé de la configuration actuelle</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[
                { label: 'Bonus parrain (1er scan)', val: `${cfg.firstScanBonusRecruiter} DH` },
                { label: 'Bonus filleul (1er scan)', val: `${cfg.firstScanBonusRecruited} DH` },
                { label: 'Scans avant bonus', val: cfg.minScansBeforeCommission === 0 ? 'Dès le 1er' : `${cfg.minScansBeforeCommission} scans` },
                { label: 'Max filleuls', val: cfg.maxRecruits === 0 ? 'Illimité' : `${cfg.maxRecruits}` },
                { label: 'Validité code', val: cfg.referralCodeExpireDays === 0 ? 'Permanent' : `${cfg.referralCodeExpireDays}j` },
              ].map(({ label, val }) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3">
                  <div className="text-xs text-gray-400 font-medium mb-1">{label}</div>
                  <div className="text-sm font-bold text-gray-800">{val}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

      {/* Referral list */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={18} className="text-green-600" />
            <h2 className="text-base font-bold text-gray-900">
              Liste des parrainages
              {allReferrals.length > 0 && (
                <span className="ml-2 text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  {allReferrals.length}
                </span>
              )}
            </h2>
          </div>
          <input
            type="search"
            placeholder="Rechercher un parrain ou filleul…"
            value={referralSearch}
            onChange={e => setReferralSearch(e.target.value)}
            className="w-56 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-400"
          />
        </div>

        {referralsLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-400 text-sm animate-pulse">
            Chargement…
          </div>
        ) : filteredReferrals.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-400 text-sm">
            {referralSearch ? 'Aucun résultat pour cette recherche.' : 'Aucun parrainage enregistré.'}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-5 py-3 font-semibold">Parrain</th>
                  <th className="text-left px-5 py-3 font-semibold">Filleul</th>
                  <th className="text-center px-4 py-3 font-semibold">Date</th>
                  <th className="text-center px-4 py-3 font-semibold">
                    <span className="flex items-center justify-center gap-1">
                      <QrCode size={12} /> Scans
                    </span>
                  </th>
                  <th className="text-center px-4 py-3 font-semibold">1er scan bonus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredReferrals.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{fullName(r.referrer)}</div>
                      {r.referrer?.email && (
                        <div className="text-xs text-gray-400">{r.referrer.email}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{fullName(r.referred)}</div>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 text-xs whitespace-nowrap">
                      {fmtDate(r.referralDate)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-block text-xs font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                        {r.scanCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.firstScanBonusPaid ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                          <Check size={11} /> Payé
                        </span>
                      ) : (
                        <span className="inline-block text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          En attente
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
